import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import FileProp from '../propTypes/fileProp';
import SkipStepButton from '../../sections/journey/overall/content/components/SkipStepButton';

const WORKER_SRC = '/dist/pdf.worker.min.mjs';

const hasFormFields = json =>
  !!json &&
  ((Array.isArray(json.formFields) && json.formFields.length > 0) ||
    (Array.isArray(json.annotations) && json.annotations.length > 0));

/**
 * In-house (Quill) drop-in for PdfViewerComponent in the user portal. Mirrors the
 * props, surrounding chrome (title / skip / mark-as-read), lesson-completion
 * behaviour, and imperative ref so call sites can swap it behind a flag with no
 * other changes.
 *
 * Lazy-loads the Quill packages in the browser only (never during SSR) so pdf.js
 * isn't evaluated server-side. Renders PdfFiller when `formFields` carries a form
 * (fill-and-sign), otherwise the read-only PdfViewer (lessons / answered PDFs).
 */
const QuillPdf = forwardRef((props, ref) => {
  const {
    t,
    id,
    documentFile,
    originalName,
    formFields,
    disabled,
    height,
    isFromCourse,
    onReadingFinished,
    onCompleteLesson,
    lessonIsDone,
    alreadyCompleted,
    onSkipStep,
  } = props;

  const [mods, setMods] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const engineRef = useRef(null);
  const completedRef = useRef(false);

  const fillMode = hasFormFields(formFields);

  useEffect(() => {
    let alive = true;
    Promise.all([
      import('@bravon-io/quill-filler'),
      import('@bravon-io/quill-viewer'),
    ])
      .then(([filler, viewer]) => {
        if (alive) {
          setMods({ PdfFiller: filler.PdfFiller, PdfViewer: viewer.PdfViewer });
        }
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to load Quill PDF', err);
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      exportFormFields: async () =>
        engineRef.current && engineRef.current.exportFormFields
          ? engineRef.current.exportFormFields()
          : null,
      checkForSignatureField: async name =>
        engineRef.current && engineRef.current.checkForSignatureField
          ? engineRef.current.checkForSignatureField(name)
          : null,
      exportPDF: async () => {
        if (!engineRef.current || !engineRef.current.exportPDF) return null;
        const file = await engineRef.current.exportPDF();
        if (!file) {
          // The filler returns null when flattening fails (e.g. the template PDF
          // couldn't be fetched, or pdf-lib choked). Surface it — otherwise the
          // save silently produces no answer + no link.
          // eslint-disable-next-line no-console
          console.error(
            '[QuillPdf] exportPDF() returned null — PDF flatten/fetch failed; see the PdfFiller error logged above.',
          );
          return null;
        }
        const formData = new FormData();
        formData.append('file', file);
        return { formData, ext: '.pdf', size: file.size };
      },
    }),
    [],
  );

  // Lesson completion parity: fire once the last page becomes visible (the
  // PSPDFKit path called onCompleteLesson when the reader reached the end).
  const markComplete = (current, total) => {
    if (completedRef.current || fillMode) return;
    if (total > 0 && current >= total) {
      completedRef.current = true;
      onCompleteLesson();
    }
  };

  const documentUrl = documentFile && documentFile.url;

  const renderEngine = () => {
    if (loadError) {
      return (
        <div style={{ padding: 24, color: '#e03131' }}>
          Failed to load the PDF.
        </div>
      );
    }
    if (!mods) {
      return (
        <div style={{ padding: 24, color: '#868e96' }}>Loading PDF…</div>
      );
    }
    const { PdfFiller, PdfViewer } = mods;
    if (fillMode) {
      return (
        <PdfFiller
          ref={engineRef}
          documentUrl={documentUrl}
          instantJSON={formFields}
          disabled={disabled}
          workerSrc={WORKER_SRC}
          height="100%"
          onError={err => {
            // eslint-disable-next-line no-console
            console.error('[QuillPdf] PdfFiller error (render or flatten/export):', err);
          }}
        />
      );
    }
    return (
      <PdfViewer
        documentUrl={documentUrl}
        workerSrc={WORKER_SRC}
        height="100%"
        onReady={pageCount => markComplete(1, pageCount)}
        onPageChange={(current, total) => markComplete(current, total)}
      />
    );
  };

  return (
    <>
      <div className="pdfViewer-title">
        <h6 className="title">{t('preview.document')}</h6>
        {onSkipStep && (
          <div className="col-6 skip-wrapper">
            <SkipStepButton onClick={onSkipStep} />
          </div>
        )}
      </div>
      <div id={id} style={{ width: '100%', height }}>
        {renderEngine()}
      </div>
      {isFromCourse && !alreadyCompleted && lessonIsDone && (
        <button
          type="button"
          onClick={onReadingFinished}
          className="btn m-btn--pill btn-dynamic"
        >
          {t('course.mark.asRead')}
        </button>
      )}
    </>
  );
});

QuillPdf.propTypes = {
  t: PropTypes.func.isRequired,
  documentFile: PropTypes.shape(FileProp.propType).isRequired,
  id: PropTypes.string.isRequired,
  originalName: PropTypes.string.isRequired,
  onReadingFinished: PropTypes.func,
  isFromCourse: PropTypes.bool,
  onCompleteLesson: PropTypes.func,
  lessonIsDone: PropTypes.bool,
  alreadyCompleted: PropTypes.bool,
  onSkipStep: PropTypes.func,
  formFields: PropTypes.shape({}),
  disabled: PropTypes.bool,
  height: PropTypes.string,
};

QuillPdf.defaultProps = {
  isFromCourse: false,
  alreadyCompleted: false,
  onReadingFinished: () => {},
  onCompleteLesson: () => {},
  lessonIsDone: false,
  onSkipStep: null,
  formFields: null,
  disabled: false,
  height: '100vh',
};

QuillPdf.displayName = 'QuillPdf';

export default QuillPdf;
